/**
 * @fileoverview "Insert Multi-fields" toolbar dropdown (template collections).
 *
 * Two-step picker: choose WHAT to repeat over (Directors, Security
 * Interests, Group Members, Signatories, …), then how it appears in the
 * document. Written to be hard to misuse:
 *
 *  - "Options block" inserts a block pre-seeded with example item fields
 *    so the author immediately sees the shape, instead of an empty box.
 *  - Item fields can never be orphaned: picking one while the cursor is
 *    inside a matching options block inserts it there; anywhere else it
 *    auto-creates an options block around it.
 *  - Values resolve on GENERATED documents (per recipient); in the
 *    template editor the chips stay as labelled placeholders.
 */

import { useState } from "react";
import { ChevronDown, ChevronLeft, Info, Repeat2 } from "lucide-react";
import type { useEditor } from "@tiptap/react";
import {
  TEMPLATE_COLLECTIONS,
  type CollectionMeta,
} from "@cvx/jobs/precedents/collectionsShared";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";
import { cn } from "@/utils/misc";

type Editor = NonNullable<ReturnType<typeof useEditor>>;

/** Singular form of a collection's noun, for plural-token defaults. */
function singularNoun(meta: CollectionMeta): string {
  const noun = meta.label.toLowerCase();
  return singularise(noun);
}

/**
 * Singular form of a collection's noun in the original casing of its
 * label (Title Case) — used to prefix inserted item-field chips so they
 * read "Director Name" instead of the generic "Item Name" (MOR / July
 * 2026: made chip labels self-describing per collection).
 */
function singularNounTitle(meta: CollectionMeta): string {
  return singularise(meta.label);
}

/** English singularisation for the collection labels we ship. */
function singularise(noun: string): string {
  if (noun.endsWith("ies")) return `${noun.slice(0, -3)}y`;
  if (noun.endsWith("s")) return noun.slice(0, -1);
  return noun;
}

/**
 * Chip label for one item field in a specific collection, e.g.
 * `chipLabel(directors, {label: "Name"})` → "Director Name".
 */
function chipLabel(meta: CollectionMeta, field: { label: string }): string {
  return `${singularNounTitle(meta)}: ${field.label}`;
}

/**
 * The collection of the repeat section the cursor currently sits inside,
 * or null when outside any repeat.
 */
function enclosingRepeatCollection(editor: Editor): string | null {
  const { $from } = editor.state.selection;
  for (let depth = $from.depth; depth > 0; depth -= 1) {
    const node = $from.node(depth);
    if (node.type.name === "repeatBlock") {
      return (node.attrs.collection as string | null) ?? null;
    }
  }
  return null;
}

export function InsertCollectionDropdown({ editor }: { editor: Editor }) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<CollectionMeta | null>(null);
  // Sub-step: choosing WHICH field "List in a sentence" joins, and how
  // the last two entries are separated ("and" vs commas only).
  const [joinPicking, setJoinPicking] = useState(false);
  const [joinStyle, setJoinStyle] = useState<"and" | "none">("and");
  // Two-field concatenation: "combine" mode holds the first pick until
  // the second click inserts `first+second` (e.g. first_name+last_name).
  const [joinCombine, setJoinCombine] = useState(false);
  const [combineFirst, setCombineFirst] = useState<{
    part: string;
    label: string;
  } | null>(null);

  const close = () => {
    setOpen(false);
    setPicked(null);
    setJoinPicking(false);
    setJoinStyle("and");
    setJoinCombine(false);
    setCombineFirst(null);
  };

  /**
   * Insert a bulleted or numbered list whose single item repeats per
   * collection entry. Seeded with the first two fields combined
   * ("A — B") to show that any mix of chips and text works inside.
   */
  const insertRepeatList = (
    meta: CollectionMeta,
    listType: "bulletList" | "orderedList",
  ) => {
    const chips = meta.fields.slice(0, 2).flatMap((field, i) => {
      const chip = {
        type: "fieldPlaceholder",
        attrs: { key: `item_${field.part}`, label: chipLabel(meta, field) },
      };
      return i === 0 ? [chip] : [{ type: "text", text: " — " }, chip];
    });
    editor
      .chain()
      .focus()
      .insertContent({
        type: listType,
        content: [
          {
            type: "listItem",
            attrs: {
              repeatType: "repeat-item",
              repeatCollection: meta.key,
            },
            content: [{ type: "paragraph", content: chips }],
          },
        ],
      })
      .run();
  };

  const insert = (run: () => void) => {
    run();
    close();
  };

  /**
   * Insert one item field. Inside a matching repeat section → plain chip
   * at the cursor. Anywhere else → a fresh repeat section seeded with
   * just that field, so the chip always has a repeat to resolve against.
   */
  const insertItemField = (
    meta: CollectionMeta,
    field: { part: string; label: string },
  ) => {
    const label = chipLabel(meta, field);
    if (enclosingRepeatCollection(editor) === meta.key) {
      editor
        .chain()
        .focus()
        .insertFieldPlaceholder({ key: `item_${field.part}`, label })
        .run();
    } else {
      editor
        .chain()
        .focus()
        .insertRepeatBlock({
          collection: meta.key,
          seedFields: [{ part: field.part, label }],
        })
        .run();
    }
  };

  const insideRepeatOfPicked =
    picked !== null && enclosingRepeatCollection(editor) === picked.key;

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setPicked(null);
          setJoinPicking(false);
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition",
            open
              ? "bg-primary/10 text-primary"
              : "text-primary/50 hover:bg-primary/5 hover:text-primary/70",
          )}
        >
          <Repeat2 className="size-3" />
          Insert Multi-fields
          <ChevronDown className="size-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[26rem] max-w-[calc(100vw-2rem)] p-0"
        align="start"
        onWheel={(e) => e.stopPropagation()}
      >
        <Command onKeyDown={(e) => e.stopPropagation()}>
          {picked === null ? (
            <>
              <CommandInput placeholder="Use to group contact fields (e.g. all directors) in doc" />
              <CommandList>
                <CommandEmpty>No collections found.</CommandEmpty>
                <CommandGroup heading="Add multiple fields for...">
                  {TEMPLATE_COLLECTIONS.map((meta) => (
                    <CommandItem
                      key={meta.key}
                      value={meta.label}
                      onSelect={() => setPicked(meta)}
                    >
                      <div className="flex flex-col">
                        <span className="text-sm">{meta.label}</span>
                        <span className="text-xs text-muted-foreground">
                          {meta.description}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
              <p className="flex items-start gap-2 border-t border-border px-3 py-2 text-[11px] leading-snug text-muted-foreground">
                <Info className="mt-0.5 size-3 shrink-0" aria-hidden />
                <span>
                  Multi-fields populate with data from the job when the document
                  is generated. They remain as placeholders in the editor view.
                </span>
              </p>
            </>
          ) : joinPicking ? (
            <>
              <button
                type="button"
                className="flex w-full items-center gap-1 border-b border-border px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground"
                onClick={() => setJoinPicking(false)}
              >
                <ChevronLeft className="size-3" />
                Back to select field types for {picked.label.toLowerCase()}
              </button>
              <div className="flex flex-wrap items-center gap-1 border-b border-border px-3 py-2">
                <span className="mr-1 text-[11px] text-muted-foreground">
                  Style:
                </span>
                <button
                  type="button"
                  className={cn(
                    "rounded px-2 py-0.5 text-[11px] font-medium",
                    joinStyle === "and"
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-muted",
                  )}
                  onClick={() => setJoinStyle("and")}
                >
                  A, B and C
                </button>
                <button
                  type="button"
                  className={cn(
                    "rounded px-2 py-0.5 text-[11px] font-medium",
                    joinStyle === "none"
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-muted",
                  )}
                  onClick={() => setJoinStyle("none")}
                >
                  A, B, C
                </button>
                <span className="mx-1 h-3 w-px bg-border" />
                <button
                  type="button"
                  className={cn(
                    "rounded px-2 py-0.5 text-[11px] font-medium",
                    joinCombine
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-muted",
                  )}
                  onClick={() => {
                    setJoinCombine((v) => !v);
                    setCombineFirst(null);
                  }}
                >
                  Combine two fields
                </button>
              </div>
              <CommandList>
                <CommandGroup
                  heading={
                    joinCombine
                      ? combineFirst
                        ? `Combining “${combineFirst.label}” with…`
                        : "Pick the FIRST field to combine"
                      : "Add fields into a sentence"
                  }
                >
                  {picked.fields.map((field) => (
                    <CommandItem
                      key={field.part}
                      value={`join ${field.label}`}
                      onSelect={() => {
                        if (joinCombine && !combineFirst) {
                          setCombineFirst(field);
                          return;
                        }
                        const part =
                          joinCombine && combineFirst
                            ? `${combineFirst.part}+${field.part}`
                            : field.part;
                        insert(() =>
                          editor
                            .chain()
                            .focus()
                            .insertCollectionJoin({
                              collection: picked.key,
                              part,
                              final: joinStyle === "none" ? "none" : "and",
                            })
                            .run(),
                        );
                      }}
                    >
                      <div className="flex flex-col">
                        <span className="text-sm">
                          {joinCombine && combineFirst
                            ? `${combineFirst.label} + ${field.label}`
                            : field.label}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {joinCombine && !combineFirst
                            ? "Then pick a second field to pair with it."
                            : (() => {
                                // Preview mirrors the actual field label so
                                // the author sees exactly how the joined
                                // sentence will read at generation time —
                                // "shown as: [full name], [full name] and
                                // [full name]" for Oxford-style, plain
                                // commas otherwise.
                                const token = `[${field.label.toLowerCase()}]`;
                                return joinStyle === "none"
                                  ? `shown as: "${token}, ${token}, ${token}"`
                                  : `shown as: "${token}, ${token} and ${token}"`;
                              })()}
                        </span>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </>
          ) : (
            <>
              <button
                type="button"
                className="flex w-full items-center gap-1 border-b border-border px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground"
                onClick={() => setPicked(null)}
              >
                <ChevronLeft className="size-3" />
                Back to selection
              </button>
              <CommandList>
                <CommandGroup heading="Select how you want multi-field to appear">
                  <CommandItem
                    value="list multi-fields"
                    onSelect={() =>
                      insert(() => {
                        // Simplest shape: one item per line, seeded with the
                        // collection's primary field (typically `name`, or
                        // `ppsr_number` for Security Interests). No bullets,
                        // no numbering — pure repeated lines.
                        const primary = picked.fields[0];
                        if (!primary) return;
                        editor
                          .chain()
                          .focus()
                          .insertRepeatBlock({
                            collection: picked.key,
                            seedFields: [
                              {
                                part: primary.part,
                                label: chipLabel(picked, primary),
                              },
                            ],
                          })
                          .run();
                      })
                    }
                  >
                    <div className="flex flex-col">
                      <span className="text-sm">List multi-fields</span>
                      <span className="text-xs text-muted-foreground">
                        One {singularNoun(picked)} per line — just the{" "}
                        {picked.fields[0]?.label.toLowerCase() ?? "primary"}{" "}
                        field. Add more fields inside once inserted.
                      </span>
                    </div>
                  </CommandItem>
                  <CommandItem
                    value="inline list in a sentence"
                    onSelect={() => setJoinPicking(true)}
                  >
                    <div className="flex flex-col">
                      <span className="text-sm">Insert in a sentence</span>
                      <span className="text-xs text-muted-foreground">
                        Add fields inline (e.g. &ldquo;[name], [name] and
                        [name]&rdquo;).
                      </span>
                    </div>
                  </CommandItem>
                  <CommandItem
                    value="bulleted list"
                    onSelect={() =>
                      insert(() => insertRepeatList(picked, "bulletList"))
                    }
                  >
                    <div className="flex flex-col">
                      <span className="text-sm">Bulleted list</span>
                      <span className="text-xs text-muted-foreground">
                        One bullet per {singularNoun(picked)} — combine any
                        fields and text inside the bullet.
                      </span>
                    </div>
                  </CommandItem>
                  <CommandItem
                    value="numbered list"
                    onSelect={() =>
                      insert(() => insertRepeatList(picked, "orderedList"))
                    }
                  >
                    <div className="flex flex-col">
                      <span className="text-sm">Numbered list</span>
                      <span className="text-xs text-muted-foreground">
                        Numbers itself 1..N — one entry per{" "}
                        {singularNoun(picked)}.
                      </span>
                    </div>
                  </CommandItem>
                  <CommandItem
                    value="repeat section"
                    onSelect={() =>
                      insert(() =>
                        editor
                          .chain()
                          .focus()
                          .insertRepeatBlock({
                            collection: picked.key,
                            seedFields: picked.fields.slice(0, 3).map((f) => ({
                              part: f.part,
                              label: chipLabel(picked, f),
                            })),
                          })
                          .run(),
                      )
                    }
                  >
                    <div className="flex flex-col">
                      <span className="text-sm">Options block</span>
                      <span className="text-xs text-muted-foreground">
                        A block that prints once per {singularNoun(picked)},
                        pre-filled with example fields you can rearrange.
                      </span>
                    </div>
                  </CommandItem>
                  <CommandItem
                    value="repeat side by side columns"
                    onSelect={() =>
                      insert(() => {
                        // Signature-bearing collections seed signature-
                        // above-name (the classic sign-off block); others
                        // stack their first two fields.
                        const hasSignature = picked.fields.some(
                          (f) => f.part === "signature",
                        );
                        const seed = hasSignature
                          ? [
                              {
                                part: "signature",
                                label: chipLabel(picked, {
                                  label: "Signature",
                                }),
                              },
                              {
                                part: "name",
                                label: chipLabel(picked, { label: "Name" }),
                              },
                            ]
                          : picked.fields.slice(0, 2).map((f) => ({
                              part: f.part,
                              label: chipLabel(picked, f),
                            }));
                        editor
                          .chain()
                          .focus()
                          .insertRepeatBlock({
                            collection: picked.key,
                            seedFields: seed,
                            seedLayout: "stacked",
                            mode: "columns",
                          })
                          .run();
                      })
                    }
                  >
                    <div className="flex flex-col">
                      <span className="text-sm">Side-by-side view</span>
                      <span className="text-xs text-muted-foreground">
                        Fields aligned next to each other in columns (e.g. two
                        signatures on one line).
                      </span>
                    </div>
                  </CommandItem>
                </CommandGroup>
                <CommandGroup
                  heading={
                    insideRepeatOfPicked
                      ? "Add a field at the cursor"
                      : `Select fields — these will repeat per ${singularNoun(picked)}`
                  }
                >
                  {picked.fields.map((field) => (
                    <CommandItem
                      key={field.part}
                      value={`field ${field.label}`}
                      onSelect={() =>
                        insert(() => insertItemField(picked, field))
                      }
                    >
                      <span className="text-sm">{field.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandGroup heading="Grammar (pure wording — adapts to the count)">
                  <CommandItem
                    value="grammar word singular plural"
                    onSelect={() =>
                      insert(() =>
                        editor
                          .chain()
                          .focus()
                          .insertCollectionPlural({
                            collection: picked.key,
                            one: singularNoun(picked),
                            many: `${singularNoun(picked)}s`,
                          })
                          .run(),
                      )
                    }
                  >
                    <div className="flex flex-col">
                      <span className="text-sm">
                        &ldquo;{singularNoun(picked)}&rdquo; ⁄ &ldquo;
                        {singularNoun(picked)}s&rdquo;
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Just the word — singular when there is 1, plural
                        otherwise.
                      </span>
                    </div>
                  </CommandItem>
                  <CommandItem
                    value="grammar count word"
                    onSelect={() =>
                      insert(() =>
                        editor
                          .chain()
                          .focus()
                          .insertCollectionPlural({
                            collection: picked.key,
                            one: `1 ${singularNoun(picked)}`,
                            many: `{count} ${singularNoun(picked)}s`,
                          })
                          .run(),
                      )
                    }
                  >
                    <div className="flex flex-col">
                      <span className="text-sm">
                        &ldquo;1 {singularNoun(picked)}&rdquo; ⁄ &ldquo;2{" "}
                        {singularNoun(picked)}s&rdquo;
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Number and word together — the count fills in
                        automatically.
                      </span>
                    </div>
                  </CommandItem>
                </CommandGroup>
                <CommandGroup heading="Auto-add a section based on count">
                  <div className="px-2 pb-1 text-[11px] leading-snug text-muted-foreground">
                    Sections turn on/off automatically from the data at
                    generation. Unlike variants, there is nothing to select.
                  </div>
                  <CommandItem
                    value="show when two or more"
                    onSelect={() =>
                      insert(() =>
                        editor
                          .chain()
                          .focus()
                          .insertConditionBlock({
                            collection: picked.key,
                            min: 2,
                          })
                          .run(),
                      )
                    }
                  >
                    <span className="text-sm">
                      …there is more than one {singularNoun(picked)} (any
                      number: 2, 5, 50…)
                    </span>
                  </CommandItem>
                  <CommandItem
                    value="show when exactly one"
                    onSelect={() =>
                      insert(() =>
                        editor
                          .chain()
                          .focus()
                          .insertConditionBlock({
                            collection: picked.key,
                            min: 1,
                            max: 1,
                          })
                          .run(),
                      )
                    }
                  >
                    <span className="text-sm">
                      …there is exactly 1 {singularNoun(picked)}
                    </span>
                  </CommandItem>
                  <CommandItem
                    value="show when none"
                    onSelect={() =>
                      insert(() =>
                        editor
                          .chain()
                          .focus()
                          .insertConditionBlock({
                            collection: picked.key,
                            min: 0,
                            max: 0,
                          })
                          .run(),
                      )
                    }
                  >
                    <span className="text-sm">
                      …there are no {picked.label.toLowerCase()}
                    </span>
                  </CommandItem>
                </CommandGroup>
              </CommandList>
            </>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
